import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import api from '../utils/api';
import { useCart } from '../contexts/CartContext';
import { toast } from 'sonner';

const PLACEHOLDER = 'https://images.pexels.com/photos/5702290/pexels-photo-5702290.jpeg?w=200';

/**
 * "Frequently Bought Together" upsell shown above the cart summary.
 * Reads the current cart, asks the backend for 3 cheap (<₹250) complementary
 * items not already in the cart, and lets the user one-tap add them.
 *
 * Pure presentational component — the parent passes the cart array so we
 * re-fetch whenever it changes.
 */
export const FrequentlyBoughtTogether = ({ cart }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { addToCart } = useCart();

  // Re-fetch whenever the cart composition changes.
  const cartKey = cart.map((i) => i.product_id).sort().join(',');
  useEffect(() => {
    if (cart.length === 0) {
      setItems([]);
      return;
    }
    setLoading(true);
    api
      .post('/cart/recommendations', {
        product_ids: cart.map((i) => i.product_id),
        limit: 3,
        max_price: 250,
      })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartKey]);

  if (!loading && items.length === 0) return null;

  return (
    <section
      data-testid="frequently-bought-together"
      className="bg-gradient-to-br from-amber-50 to-orange-50 border border-orange-200 rounded-xl p-5 mb-4"
    >
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="text-orange-600" size={18} />
        <h3 className="font-bold text-orange-900">Frequently Bought Together</h3>
        <span className="text-xs text-gray-500 ml-1">Complete your puja</span>
      </div>

      {loading ? (
        <div className="text-sm text-gray-400 py-2">Looking for great add-ons…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {items.map((p) => (
            <div
              key={p.id}
              data-testid={`fbt-item-${p.id}`}
              className="bg-white rounded-lg border border-orange-100 p-3 flex gap-3 items-center"
            >
              <Link to={`/product/${p.id}`} className="shrink-0">
                <img
                  src={p.images?.[0] || p.image_url || PLACEHOLDER}
                  alt={p.name}
                  className="w-16 h-16 object-cover rounded-md"
                />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${p.id}`}>
                  <p className="text-sm font-medium text-gray-800 hover:text-orange-700 line-clamp-2 leading-tight">
                    {p.name}
                  </p>
                </Link>
                <p className="text-xs text-gray-500 mt-1">
                  <span className="font-semibold text-orange-700">
                    ₹{p.sale_price || p.price}
                  </span>
                  {p.sale_price && (
                    <span className="line-through ml-1 text-gray-400">₹{p.price}</span>
                  )}
                </p>
                <Button
                  data-testid={`fbt-add-${p.id}`}
                  size="sm"
                  onClick={() => {
                    addToCart(p);
                    toast.success(`${p.name} added`);
                  }}
                  className="mt-2 h-7 px-2 text-xs bg-orange-600 hover:bg-orange-700"
                >
                  <Plus size={12} className="mr-1" /> Add ₹{p.sale_price || p.price}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
